import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import FeeRuleTable from './FeeRuleTable';
import EventGeneralLedgerSettings, { PRICE_BANDS_MODE } from './EventGeneralLedgerSettings';
import EventFundraisingSettings from './EventFundraisingSettings';
import VenueAgreementsEditor from '../../shared/components/VenueAgreementsEditor';
import TixelWaitlistRedirectToggle from '../../shared/components/TixelWaitlistRedirectToggle';
import { LOCATION_PRIVACY_OPTIONS } from '../../shared/constants/locationPrivacyOptions';

import Flatpickr from "react-flatpickr";
import MaskedInput from 'react-text-mask'
import { Button, Select, TextInput, Toggle, Tooltip } from '@opendate-app/setlist';
import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';
import { maxTicketsError } from '../../../shared/maxTicketsError';

const LOCATION_REVEALED_TOOLTIP =
  "This location has already been revealed and ticket holders have been emailed the " +
  "address, so it cannot be hidden again. Contact support if this event needs to be " +
  "made secret again.";

const CHECK_IN_PERIOD_OPTIONS = [
  { value: "all_time", label: "All Time" },
  { value: "daily", label: "Daily" },
];

const sectionTitleStyle = { "fontSize": "14px" };
const descriptionStyle = { "fontSize": "12px", "maxWidth": "450px", "color": "var(--sl-text-tertiary)" };
const noteStyle = { "fontSize": "12px", "marginTop": "6px", "color": "var(--sl-text-tertiary)" };
const fieldLabelStyle = { "fontSize": "14px", "fontWeight": 500, "marginTop": "18px", "color": "var(--sl-text-secondary)" };
const dividerStyle = {
  "borderTop": "1px solid var(--sl-border-secondary)",
  "marginTop": "25px",
  "marginBottom": "32px"
};

const SettingsTab = ({
  csrfToken,
  confirm,
  team,
  updateConfirm,
  autoSaveConfirm,
  feeRuleSets,
  confirmChanged
}) => {
  const [maxTicketsAllowed, setMaxTicketsAllowed] = useState(confirm.max_tickets_per_email);
  const latestConfirm = useRef(confirm);
  latestConfirm.current = confirm;
  const [eventFeeComponentCount, setEventFeeComponentCount] = useState(
    team.can_access_general_ledger_codes ? null : 0
  );
  const [organizerFeeMode, setOrganizerFeeMode] = useState(null);
  const showsPriceBands = !team.can_access_general_ledger_codes ||
    organizerFeeMode === PRICE_BANDS_MODE ||
    eventFeeComponentCount === 0;
  const canEnableCheckoutCaptcha = team.can_enable_checkout_captcha ?? true;

  // reveal_location_at arrives as a server string but Flatpickr needs a Date in the
  // venue's time zone. Derived at render rather than converted once into Redux on
  // mount: any PATCH response replaces confirm wholesale, so a string can reappear at
  // any time, and a mount-only effect would leave the picker unrendered until reload.
  const revealLocationAt = typeof confirm.reveal_location_at === "string"
    ? offsetDateForBrowser(confirm.reveal_location_at)
    : confirm.reveal_location_at;

  const canSubmitForm = (
    String(maxTicketsAllowed ?? "") !== String(confirm.max_tickets_per_email ?? "")
      && !maxTicketsError(maxTicketsAllowed)
  );

  // A reveal is one-way, so the selector locks once ticket holders have been emailed
  // the address. CalendarEvent#location_privacy_locked_after_reveal enforces the same
  // rule server-side.
  const locationRevealed = !!confirm.location_revealed_at;
  const locationPrivacy = confirm.location_privacy || "visible";

  // Hiding an address is only safe if something will tell ticket holders where to go
  // when it is revealed, so the feature stays off until the venue has an active
  // Event Location Revealed campaign. Gated on turning it ON only: an event already
  // using a secret location must still be revealable even if that campaign was later
  // paused or deleted, or the location would be stuck secret forever.
  const revealCampaignConfigured = !!team.location_reveal_campaign_configured;
  const blockedForMissingCampaign = !revealCampaignConfigured && locationPrivacy === "visible";

  // Revealing by hand is the same irreversible act as letting the schedule do it, so
  // warn first. Only a published, public event actually emails ticket holders — and
  // only then does the server lock the setting — so the copy matches what happens.
  // Mirrors CalendarEvent#published?, which is published_at <= now, not merely set:
  // a future published_at neither notifies nor locks.
  const confirmManualReveal = () => {
    const isPublished = !!confirm.published_at &&
      new Date(confirm.published_at) <= new Date();
    const willNotifyTicketHolders = isPublished && !!confirm.is_public;

    return window.confirm(
      willNotifyTicketHolders
        ? "Reveal this location now?\n\n" +
          "The real venue name and address become public everywhere fans see this " +
          "event, and ticket holders are emailed the address immediately.\n\n" +
          "This cannot be undone. The setting locks afterward — contact support if " +
          "the event ever needs to be made secret again."
        : "Reveal this location now?\n\n" +
          "The real venue name and address become public everywhere fans see this event."
    );
  };

  const locationPrivacySelect = (
    <Select
      id="confirm-location-privacy"
      label="Location Privacy"
      width={260}
      disabled={locationRevealed || blockedForMissingCampaign}
      options={LOCATION_PRIVACY_OPTIONS}
      value={locationPrivacy}
      onChange={(newValue) => {
        if (newValue === locationPrivacy) return;

        const isManualReveal = newValue === "visible" && locationPrivacy !== "visible";
        if (isManualReveal && !confirmManualReveal()) return;

        const updated = { ...confirm, location_privacy: newValue };
        updateConfirm(updated);
        // Read location_revealed_at back off the response instead of assuming it. The
        // server stamps it only for a published, public Confirm, which is exactly when
        // the setting locks — so the select disables the moment a real reveal lands,
        // and stays editable while the event is still a draft.
        autoSaveConfirm(csrfToken, team, updated, false, (data) => {
          if (data && data.location_revealed_at) {
            updateConfirm({ ...updated, location_revealed_at: data.location_revealed_at });
          }
        }, { includeLocationPrivacy: true });
      }}
    />
  );

  return (
    <div style={{"color": "var(--sl-text-primary)"}}>
      <form onSubmit={
              (e) => {
                e.preventDefault();

                if(canSubmitForm) {
                  var updated = {
                    ...confirm,
                    max_tickets_per_email: maxTicketsAllowed
                  }

                  updateConfirm(updated);
                  autoSaveConfirm(csrfToken, team, updated);
                }
              }
            }>
        <div className='row'>
          <div className='col-12' style={{"marginTop": "20px"}}>
            <p className='mb-2' style={sectionTitleStyle}>
              <strong>Maximum Tickets Allowed</strong>
            </p>
            <p className='mb-0' style={descriptionStyle}>
              Define the maximum number of tickets that can be purchased by a fan across multiple orders. If a fan attempts to purchase more than the maximum number of tickets allowed, they will receive an alert notifying them that the event has a maximum and the order will not process.
            </p>
            <div style={{"maxWidth": "360px", "marginTop": "18px"}}>
              <TextInput
                as={MaskedInput}
                id="venue-ownership-max-tickets-per-email"
                label="Maximum Tickets Allowed"
                mask={[/[0-9]/, /[0-9]/, /[0-9]/]}
                guide={false}
                value={maxTicketsAllowed ?? ""}
                error={String(maxTicketsAllowed ?? "") !== String(confirm.max_tickets_per_email ?? "") ? maxTicketsError(maxTicketsAllowed) : null}
                onChange={
                  (e) => {
                    setMaxTicketsAllowed(e.target.value);
                  }
                }
                placeholder="No limit"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md" style={{"marginTop": "20px"}}>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!canSubmitForm}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </form>
      <hr style={dividerStyle} />
      <div className='row'>
        <div className='col-12' style={{"marginTop": "20px"}}>
          <p className='mb-2' style={sectionTitleStyle}>
            <strong>Check-in Period</strong>
          </p>
          <p className='mb-0' style={descriptionStyle}>
            Controls how ticket check-in counts are tracked. "All Time" counts every successful check-in against the scan limit. "Daily" resets the count each day at 4 AM venue local time. Changing this setting updates all existing ticket types and add-ons on this event, and is inherited by any new ones.
          </p>
          <div style={{"marginTop": "18px"}}>
            <Select
              id="confirm-check-in-period"
              label="Check-in Period"
              width={200}
              options={CHECK_IN_PERIOD_OPTIONS}
              value={confirm.check_in_period || "all_time"}
              onChange={(newValue) => {
                if (newValue === (confirm.check_in_period || "all_time")) return;

                const message = "This will update the check-in period for all existing ticket types and add-ons on this event. Continue?";
                if (!window.confirm(message)) return;

                const updated = { ...confirm, check_in_period: newValue };
                updateConfirm(updated);
                autoSaveConfirm(csrfToken, team, updated);
              }}
            />
          </div>
        </div>
      </div>
      <hr style={dividerStyle} />
      <div className='row'>
        <div className='col-12' style={{"marginTop": "20px"}}>
          <p className='mb-2' style={sectionTitleStyle}>
            <strong>Location Privacy</strong>
          </p>
          <p className='mb-0' style={descriptionStyle}>
            Hide the venue address for events promoted as a secret location. "City & state only" hides the street address and map but still shows the city and state; "Hide location entirely" hides all address information. The venue name is replaced with the label below, and fans see a note that the exact location will be announced before the event. This applies everywhere fans see the event, including confirmation emails and tickets, until you switch it back.
          </p>
          <div className="row">
            <div className="col-12 col-md-6" style={{"marginTop": "18px"}}>
              {locationRevealed ? (
                <Tooltip title={LOCATION_REVEALED_TOOLTIP} maxWidth={320}>
                  <div style={{"width": "260px"}} tabIndex={0}>
                    {locationPrivacySelect}
                  </div>
                </Tooltip>
              ) : locationPrivacySelect}
              {blockedForMissingCampaign ? (
                <p className='mb-0' style={{...noteStyle, "maxWidth": "340px", "color": "var(--sl-text-warning)"}}>
                  To hide a location you first need an active <strong>Event Location Revealed</strong> campaign, so ticket holders are automatically sent the address when you reveal it.
                  {" "}
                  <a href={`/teams/${team.id}/campaigns`} target="_blank" rel="noopener noreferrer">
                    Create and activate that campaign
                  </a>
                  {" "}
                  and this setting unlocks when you come back.
                </p>
              ) : null}
            </div>
            {confirm.location_privacy && confirm.location_privacy !== "visible" ? (
              <div className="col-12 col-md-6" style={{"marginTop": "18px"}}>
                <div style={{"width": "260px"}}>
                  <TextInput
                    id="confirm-secret-location-label"
                    label="Location Label"
                    maxLength={100}
                    placeholder="Secret Location"
                    hint='Shown to fans in place of the venue name. Defaults to "Secret Location".'
                    value={confirm.secret_location_label || ""}
                    onChange={(e) => updateConfirm({ ...confirm, secret_location_label: e.target.value })}
                    onBlur={() => autoSaveConfirm(csrfToken, team, confirm)}
                  />
                </div>
              </div>
            ) : null}
          </div>
          {confirm.location_privacy && confirm.location_privacy !== "visible" ? (
            <>
            <p className='mb-0' style={{...noteStyle, "marginTop": "10px", "maxWidth": "450px", "color": "var(--sl-text-warning)"}}>
              <strong>Revealing the location cannot be undone.</strong> Ticket holders are emailed the address as soon as it is revealed, and this setting locks afterward. Contact support if a revealed event ever needs to be made secret again.
            </p>
            <div className="row">
              <div className="col-12 col-md-6">
                <label htmlFor="confirm-reveal-location-at"
                       className="d-block mb-1"
                       style={fieldLabelStyle}>
                  Reveal Location At
                </label>
                <div className="d-flex align-items-center" style={{"width": "260px", "gap": "8px"}}>
                    <Flatpickr
                      id="confirm-reveal-location-at"
                      options={{
                        dateFormat: "M j, Y at h:iK",
                        enableTime: true,
                        // The picker's value is a Date whose local wall clock is the
                        // VENUE's wall clock, so "now" has to be expressed the same way.
                        // A bare new Date() is the browser's clock: east of the venue it
                        // greys out legitimate times, west of it the picker offers times
                        // that convert into the past and the server rejects.
                        minDate: offsetDateForBrowser(new Date()),
                        onOpen: function(selectedDates, dateStr, instance) {
                          instance.set("minDate", offsetDateForBrowser(new Date()));
                        },
                      }}
                      placeholder={"Choose date & time (optional)"}
                      className={"form-control ticketing-date-picker form-control-flatpickr"}
                      style={{"fontSize": "14px"}}
                      value={revealLocationAt}
                      onClose={dates => {
                        const updated = { ...confirm, reveal_location_at: dates[0] || null };
                        updateConfirm(updated);
                        autoSaveConfirm(csrfToken, team, updated, false, () => {}, { includeRevealLocationAt: true });
                      }}
                    />
                  {revealLocationAt ? (
                    <Button
                      variant="link-color"
                      size="sm"
                      onClick={() => {
                        const updated = { ...confirm, reveal_location_at: null };
                        updateConfirm(updated);
                        autoSaveConfirm(csrfToken, team, updated, false, () => {}, { includeRevealLocationAt: true });
                      }}
                    >
                      Clear
                    </Button>
                  ) : null}
                </div>
                <p className='mb-0' style={{...noteStyle, "maxWidth": "340px"}}>
                  Opendate switches this event back to "Show full address" at this time. Leave blank to reveal the location yourself.
                </p>
                <p className='mb-0' style={{...noteStyle, "maxWidth": "340px"}}>
                  Set up the <strong>Event Location Revealed</strong> campaign automation
                  {" "}
                  <a href={`/teams/${team.id}/campaigns`} target="_blank" rel="noopener noreferrer">
                    in Automations
                  </a>
                  {" "}
                  before this time, so ticket holders are sent the address.
                </p>
              </div>
            </div>
            </>
          ) : null}
        </div>
      </div>
      {canEnableCheckoutCaptcha || confirm.checkout_captcha_enabled ? (
        <>
          <hr style={dividerStyle} />
          <div className='row'>
            <div className='col-12' style={{"marginTop": "20px"}}>
              <p className='mb-2' style={sectionTitleStyle}>
                <strong>Checkout CAPTCHA</strong>
              </p>
              {!canEnableCheckoutCaptcha && confirm.checkout_captcha_enabled ? (
                <p style={descriptionStyle}>
                  Checkout CAPTCHA is enabled on this event, but your team no longer has access to this feature. You can disable it below.
                </p>
              ) : (
                <p style={descriptionStyle}>
                  CAPTCHA verification protects your event from fraud and abuse. Opendate recommends enabling checkout CAPTCHA for high demand events to prevent bots from purchasing tickets. Verification is powered by Cloudflare Turnstile.
                </p>
              )}
              <Toggle
                id="confirm-checkout-captcha-enabled"
                label={confirm.checkout_captcha_enabled ? "Checkout CAPTCHA Enabled" : "Checkout CAPTCHA Disabled"}
                checked={Boolean(confirm.checkout_captcha_enabled)}
                disabled={!canEnableCheckoutCaptcha && !confirm.checkout_captcha_enabled}
                onChange={
                  (e) => {
                    if (!canEnableCheckoutCaptcha && e.target.checked) return;

                    const updated = { ...confirm, checkout_captcha_enabled: e.target.checked };
                    updateConfirm(updated);
                    autoSaveConfirm(csrfToken, team, updated);
                  }
                }
              />
            </div>
          </div>
        </>
      ) : null}
      <hr style={dividerStyle} />
      <div className='row'>
        <div className='col-12' style={{"marginTop": "20px"}}>
          <p className='mb-2' style={sectionTitleStyle}>
            <strong>Sold-Out Waitlist</strong>
          </p>
          <p style={descriptionStyle}>
            When this event is sold out, show a "Get on the waitlist" signup form on the checkout page. This follows your team's default until you change it here.
          </p>
          <Toggle
            id="confirm-collect-waitlist-interest"
            label={confirm.collect_waitlist_interest_effective ? "Waitlist Enabled" : "Waitlist Disabled"}
            checked={Boolean(confirm.collect_waitlist_interest_effective)}
            onChange={(e) => {
              const updated = {
                ...confirm,
                collect_waitlist_interest_enabled: e.target.checked,
                collect_waitlist_interest_effective: e.target.checked
              };
              updateConfirm(updated);
              autoSaveConfirm(csrfToken, team, updated);
            }}
          />
        </div>
      </div>
      <hr style={dividerStyle} />
      <div className='row'>
        <div className='col-12' style={{"marginTop": "20px"}}>
          <p className='mb-2' style={sectionTitleStyle}>
            <strong>Support Acts Under Event Title</strong>
          </p>
          <p style={descriptionStyle}>
            Show the support acts from this event's lineup as a "with ..." line under the event title on the checkout page, tickets, and ticket emails. Turn this off when the support acts are already named in the event title. The lineup itself is unchanged, so settlement, reporting, and Bandsintown still see every performer.
          </p>
          <Toggle
            id="confirm-show-support-acts-under-title"
            label={confirm.show_support_acts_under_title ? "Support Acts Shown" : "Support Acts Hidden"}
            checked={Boolean(confirm.show_support_acts_under_title)}
            onChange={(e) => {
              const updated = { ...confirm, show_support_acts_under_title: e.target.checked };
              updateConfirm(updated);
              autoSaveConfirm(csrfToken, team, updated, false, (data) => {
                updateConfirm({ ...updated, support_artist_names: data.support_artist_names });
              });
            }}
          />
        </div>
      </div>
      <hr style={dividerStyle} />
      <div className='row'>
        <div className='col-12' style={{"marginTop": "20px"}}>
          <p className='mb-2' style={sectionTitleStyle}>
            <strong>Allow single empty seats</strong>
          </p>
          <p style={descriptionStyle}>
            On reserved-seating shows, let fans buy seats that leave one empty seat next to their group. Smaller parties can buy the seats they want, but you may end up with single seats that are hard to sell.
          </p>
          <Toggle
            id="confirm-allow-orphan-seats"
            label={confirm.allow_orphan_seats ? "Single Empty Seats Allowed" : "Following Team Default"}
            checked={Boolean(confirm.allow_orphan_seats)}
            onChange={(e) => {
              const updated = { ...confirm, allow_orphan_seats: e.target.checked };
              updateConfirm(updated);
              autoSaveConfirm(csrfToken, team, updated);
            }}
          />
        </div>
      </div>
      {team.tixel_enabled ? (
        <>
          <hr style={dividerStyle} />
          <div className='row'>
            <div className='col-12' style={{"marginTop": "20px"}}>
              <p className='mb-2' style={sectionTitleStyle}>
                <strong>Tixel Waitlist Redirect</strong>
              </p>
              <p style={descriptionStyle}>
                When this event is sold out, replace the "Get on the waitlist" signup form with a button linking fans to this event's Tixel resale page. This is on for every show by default; turn it off to collect a waitlist on this event instead.
              </p>
              <TixelWaitlistRedirectToggle
                id="confirm-tixel-waitlist-redirect"
                checked={confirm.tixel_waitlist_redirect_effective}
                onChange={
                  (checked) => {
                    const updated = {
                      ...confirm,
                      tixel_waitlist_redirect_enabled: checked,
                      tixel_waitlist_redirect_effective: checked
                    };
                    updateConfirm(updated);
                    autoSaveConfirm(csrfToken, team, updated);
                  }
                }
              />
            </div>
          </div>
        </>
      ) : null}
      <hr style={dividerStyle} />
      {confirm.venue_ownership_id ? (
        <div className='row'>
          <div className='col-12'>
            <p className='mb-1' style={{"fontSize": "16px"}}>
              <strong>Event Agreements</strong>
            </p>
            <p style={{...descriptionStyle, "fontSize": "12px", "maxWidth": "560px"}}>
              Agreements shown on this event's checkout only, in addition to the venue-wide
              agreements set in Ticketing Settings.
            </p>
            <VenueAgreementsEditor
              csrfToken={csrfToken}
              teamId={team.id}
              venueOwnershipId={confirm.venue_ownership_id}
              calendarEventId={confirm.id}
            />
          </div>
        </div>
      ) : null}
      {team.can_access_giving_campaigns ? (
        <>
          <hr style={dividerStyle} />
          <EventFundraisingSettings
            csrfToken={csrfToken}
            confirm={confirm}
            team={team}
            onSaved={givingCampaignId => updateConfirm({ ...latestConfirm.current, giving_campaign_id: givingCampaignId })}
          />
        </>
      ) : null}
      <hr style={dividerStyle} />
      {team.can_access_general_ledger_codes ? (
        <EventGeneralLedgerSettings
          csrfToken={csrfToken}
          confirm={confirm}
          team={team}
          onComponentsChange={setEventFeeComponentCount}
          onModeChange={setOrganizerFeeMode}
        />
      ) : null}
      {showsPriceBands ? (
        <FeeRuleTable
          csrfToken={csrfToken}
          confirm={confirm}
          feeRuleSets={feeRuleSets}
          team={team}
          eventFeeComponentCount={eventFeeComponentCount}
        />
      ) : null}
    </div>
  );
}

SettingsTab.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  updateConfirm: PropTypes.func.isRequired,
  autoSaveConfirm: PropTypes.func.isRequired,
  feeRuleSets: PropTypes.array,
  confirmChanged: PropTypes.func.isRequired
};

export default SettingsTab;
